import { memo, useMemo } from 'react';
import styled from 'styled-components';
import { Tooltip } from 'react-tooltip';
import InfoTooltip from '@components/infoTooltip/InfoTooltip';
import { getCountingMethodConfig } from './countingMethodConfig';

/**
 * Binary-search for the last chartData entry at or before the given time (seconds).
 * Returns the cumulative value at that point, or 0 if before the first sample.
 * Assumes chartData is sorted by timestamp_sec.
 */
const getCumulativeValue = (data, time, cumulativeField) => {
	let left = 0;
	let right = data.length - 1;
	let result = 0;

	while (left <= right) {
		const mid = Math.floor((left + right) / 2);
		if (data[mid].timestamp_sec <= time) {
			result = data[mid][cumulativeField] ?? result;
			left = mid + 1;
		} else {
			right = mid - 1;
		}
	}

	return result;
};

const TOOLTIP_ID = 'tooltip-key-metrics';

const METRIC_TOOLTIPS = {
	uniqueCount: 'Number of distinct animals tracked for at least 1 second',
	totalCount: 'Sum of all detections across every frame',
	average: 'Average number of objects detected per video frame',
	peakTime: 'Timestamp where the highest number of simultaneous detections occurred',
	maxSimultaneousCount: 'Highest number of objects visible in a single frame',
	countingMethod: 'Algorithm used to count objects in the video',
	lineCrossNet: 'Animals that passed the line: crossings one way minus crossings back, per line',
	lineCrossCount:
		'Running count of line crossing events up to the current playback position, including back-and-forth crossings',
};

const Column = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	min-height: 0;
	overflow-y: auto;
`;

const Card = styled.div`
	background: ${({ $focused }) =>
		$focused ? 'rgba(32, 172, 107, 0.08)' : 'rgba(152, 152, 152, 0.05)'};
	border: 1px solid ${({ theme }) => theme.colors.border};
	border-left: 3px solid
		${({ $focused, theme }) => ($focused ? theme.colors.green : 'transparent')};
	border-radius: 8px;
	padding: 12px;
	text-align: center;
`;

const MetricLabel = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 4px;
	font-size: 10px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	color: ${({ theme }) => theme.colors.textAccent};
	opacity: 0.8;

	svg {
		font-size: 12px;
		opacity: 0.6;
		flex-shrink: 0;
		cursor: help;
	}
`;

const MetricValue = styled.div`
	font-size: ${({ $focused, $small }) => ($focused ? '30px' : $small ? '16px' : '22px')};
	font-weight: 700;
	color: ${({ $highlight, theme }) => ($highlight ? theme.colors.green : '#fff')};
	margin: 2px 0;
`;

const KeyMetrics = memo(
	({
		totalCount,
		uniqueCount,
		lineCrossNet,
		average,
		peakTime,
		maxSimultaneousCount,
		countingMethod,
		chartData,
		currentTime,
	}) => {
		const methodConfig = useMemo(
			() => getCountingMethodConfig(countingMethod),
			[countingMethod]
		);

		const lineCrossCount = useMemo(() => {
			if (!chartData?.length || currentTime == null) return null;
			return getCumulativeValue(chartData, currentTime, methodConfig.cumulativeField);
		}, [chartData, currentTime, methodConfig]);

		const { focusedMetric } = methodConfig;

		return (
			<Column>
				<Card>
					<MetricLabel>
						Detection Method
						<InfoTooltip
							tooltipContainerId={TOOLTIP_ID}
							content={METRIC_TOOLTIPS.countingMethod}
							placement="top"
						/>
					</MetricLabel>
					<MetricValue $small>{methodConfig.methodLabel}</MetricValue>
				</Card>
				{countingMethod === 'line_based' && (
					<Card $focused={focusedMetric === 'lineCrossNet'}>
						<MetricLabel>
							Animals Passed
							<InfoTooltip
								tooltipContainerId={TOOLTIP_ID}
								content={METRIC_TOOLTIPS.lineCrossNet}
								placement="top"
							/>
						</MetricLabel>
						<MetricValue $focused={focusedMetric === 'lineCrossNet'} $highlight>
							{lineCrossNet ?? '—'}
						</MetricValue>
					</Card>
				)}
				{countingMethod === 'line_based' && (
					<Card $focused={focusedMetric === 'lineCrossCount'}>
						<MetricLabel>
							Line Crossings
							<InfoTooltip
								tooltipContainerId={TOOLTIP_ID}
								content={METRIC_TOOLTIPS.lineCrossCount}
								placement="top"
							/>
						</MetricLabel>
						<MetricValue $focused={focusedMetric === 'lineCrossCount'} $highlight>
							{lineCrossCount ?? '—'}
						</MetricValue>
					</Card>
				)}
				{countingMethod === 'unique_id' && (
					<Card $focused={focusedMetric === 'uniqueCount'}>
						<MetricLabel>
							Unique Animals
							<InfoTooltip
								tooltipContainerId={TOOLTIP_ID}
								content={METRIC_TOOLTIPS.uniqueCount}
								placement="top"
							/>
						</MetricLabel>
						<MetricValue $focused={focusedMetric === 'uniqueCount'} $highlight>
							{uniqueCount ?? '—'}
						</MetricValue>
					</Card>
				)}
				<Card $focused={focusedMetric === 'maxSimultaneousCount'}>
					<MetricLabel>
						Max Simul. Detections
						<InfoTooltip
							tooltipContainerId={TOOLTIP_ID}
							content={METRIC_TOOLTIPS.maxSimultaneousCount}
							placement="top"
						/>
					</MetricLabel>
					<MetricValue $focused={focusedMetric === 'maxSimultaneousCount'} $highlight>
						{maxSimultaneousCount ?? '—'}
					</MetricValue>
				</Card>
				<Card>
					<MetricLabel>
						Total Detections
						<InfoTooltip
							tooltipContainerId={TOOLTIP_ID}
							content={METRIC_TOOLTIPS.totalCount}
							placement="top"
						/>
					</MetricLabel>
					<MetricValue $highlight>{totalCount ?? '—'}</MetricValue>
				</Card>
				<Card>
					<MetricLabel>
						Avg Detections / Frame
						<InfoTooltip
							tooltipContainerId={TOOLTIP_ID}
							content={METRIC_TOOLTIPS.average}
							placement="top"
						/>
					</MetricLabel>
					<MetricValue>{average ?? '—'}</MetricValue>
				</Card>
				<Card>
					<MetricLabel>
						Peak Detection Time
						<InfoTooltip
							tooltipContainerId={TOOLTIP_ID}
							content={METRIC_TOOLTIPS.peakTime}
							placement="top"
						/>
					</MetricLabel>
					<MetricValue $small>{peakTime ?? '—'}</MetricValue>
				</Card>
				<Tooltip id={TOOLTIP_ID} variant="dark" className="small-tooltip" />
			</Column>
		);
	}
);

KeyMetrics.displayName = 'KeyMetrics';

export default KeyMetrics;
